import React, { useRef, useState, useEffect } from 'react';
import { ScrollText, Check, X } from 'lucide-react';

const RULES = [
  { title: 'Donation-only listings', body: 'All items in the auction store are donated. Donors receive no monetary compensation. 100% of proceeds go to the Community Fund and the Youth Fund, split equally.' },
  { title: 'Automated pricing', body: 'The platform sets the starting auction price automatically based on your declared item value. Donors cannot set or change prices. Admins may override in rare cases.' },
  { title: 'One complaint = suspension', body: 'One verified complaint or violation results in automatic suspension of your listing privileges. You may appeal from your profile.' },
  { title: 'Appeal process', body: 'Suspended users can submit an appeal from their profile. Admin will review and either reinstate you or issue a permanent ban.' },
  { title: 'Community flagging', body: 'Any listing that receives 3 or more flags from different users is automatically pulled and sent to admin review. Repeated false flags can lead to loss of flagging privilege.' },
  { title: 'Confirmation code required', body: 'When a buyer wins, they receive a secret pickup code. For local pickup, the buyer gives the donor their code at handoff. For shipped items, the carrier confirms delivery automatically.' },
  { title: 'Refund policy', body: 'Buyers receive a full refund if the item was misrepresented, damaged, or not as described. Refunds are covered from auction/Deal Bin revenue, not from the donor.' },
  { title: 'Fraud penalties', body: 'Proven fraud results in permanent ban, complete removal of all points, and blocked re-registration attempts. Sellers who violate operations are permanently removed with no compensation.' },
];

export default function RulesModal({ open, onAccept, onClose }) {
  const bodyRef = useRef(null);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!open) {
      setScrolledEnd(false);
      setAccepting(false);
    }
  }, [open]);

  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    // consider "reached bottom" within 16px tolerance
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 16) setScrolledEnd(true);
  };

  const doAccept = async () => {
    setAccepting(true);
    try { await onAccept(); } finally { setAccepting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
      <div data-testid="rules-modal" className="w-full max-w-lg bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <div className="flex items-center gap-2">
            <ScrollText size={18} className="text-green-400" />
            <div className="font-semibold text-white">Auction Rules</div>
          </div>
          {onClose && (
            <button data-testid="rules-modal-close" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
          )}
        </div>
        <div
          ref={bodyRef}
          onScroll={handleScroll}
          data-testid="rules-modal-body"
          className="max-h-[55vh] overflow-y-auto px-5 py-4 space-y-4 text-sm text-gray-200"
        >
          <p className="text-xs text-gray-400">Please scroll to the bottom and click Accept before submitting an auction listing.</p>
          {RULES.map((r, i) => (
            <div key={i}>
              <div className="font-semibold text-white text-sm">{i + 1}. {r.title}</div>
              <p className="mt-1 text-sm text-gray-300 leading-relaxed">{r.body}</p>
            </div>
          ))}
          <p className="text-[11px] text-gray-500 pt-2 border-t border-[#1f1f1f]">By clicking Accept you acknowledge you have read and agree to these rules. Your acceptance is logged with a timestamp.</p>
        </div>
        <div className="px-5 py-4 border-t border-[#1f1f1f] flex items-center justify-end gap-2">
          {!scrolledEnd ? (
            <div data-testid="rules-modal-hint" className="text-xs text-amber-400">Scroll to the bottom to enable Accept</div>
          ) : null}
          <button
            data-testid="rules-modal-accept"
            onClick={doAccept}
            disabled={!scrolledEnd || accepting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 text-black text-sm font-semibold"
          >
            <Check size={14} /> {accepting ? 'Saving…' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
}
